<template>
  <div class="row g-3 mb-25">
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_sales`) }}</span>
        <strong class="summary-value text-success">${{ formatNumber(summary.sales) }}</strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_sales_return`) }}</span>
        <strong class="summary-value text-danger">${{ formatNumber(summary.sales_returns) }}</strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_income`) }}</span>
        <strong class="summary-value" :class="includesIncome ? 'text-primary' : 'text-muted'">
          ${{ formatNumber(summary.income) }}
        </strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_purchases`) }}</span>
        <strong class="summary-value text-danger">${{ formatNumber(summary.purchases) }}</strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_expenses`) }}</span>
        <strong class="summary-value text-danger">${{ formatNumber(summary.expenses) }}</strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-4 col-sm-6">
      <div class="summary-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_net_sales`) }}</span>
        <strong class="summary-value text-success">${{ formatNumber(summary.sales - summary.sales_returns) }}</strong>
      </div>
    </div>
    <div class="col-xl-2 col-md-8 col-sm-12">
      <div class="summary-card profit-card">
        <span class="summary-label">{{ $t(`${i18nScope}.summary_net_profit`) }}</span>
        <strong class="summary-value" :class="summary.net_profit >= 0 ? 'text-success' : 'text-danger'">
          ${{ formatNumber(summary.net_profit) }}
        </strong>
      </div>
    </div>
  </div>

  <div v-if="!includesIncome" class="income-note mb-25">
    {{ $t(`${i18nScope}.income_note`) }}
  </div>

  <div class="card border-0 shadow-none rounded-1 mb-25">
    <div class="card-body p-xl-40">
      <a-table
        :columns="columns"
        :data-source="rows"
        :row-key="'month'"
        :loading="loading"
        :pagination="false"
        :scroll="{ x: 980 }"
        class="text-nowrap align-middle profit-loss-table"
      >
        <template #emptyText>
          <div class="py-4 text-center text-muted">{{ $t(`${i18nScope}.no_profit_loss_data_found`) }}</div>
        </template>

        <template #bodyCell="{ column, record }">
          <template v-if="column && column.key === 'month'">
            {{ formatMonth(record.month) }}
          </template>

          <template v-else-if="column && monetaryColumns.includes(String(column.key))">
            <span :class="getMoneyClass(String(column.key), record)">
              ${{ formatNumber(Number(record[String(column.key) as keyof typeof record] || 0)) }}
            </span>
          </template>
        </template>
      </a-table>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent } from "vue";
import { useI18n } from "vue-i18n";
import type { ProfitLossReportItem } from "@/services/report";

export default defineComponent({
  name: "ProfitLoss",
  props: {
    rows: {
      type: Array as () => ProfitLossReportItem[],
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
    includesIncome: {
      type: Boolean,
      default: true,
    },
    summary: {
      type: Object as () => {
        sales: number;
        sales_returns: number;
        income: number;
        purchases: number;
        expenses: number;
        net_profit: number;
      },
      required: true,
    },
    i18nScope: {
      type: String,
      default: "profit_loss_report_page",
    },
  },
  setup(props) {
    const { t, locale } = useI18n();

    const columns = computed(() => [
      { title: t(`${props.i18nScope}.table_month`), key: "month", dataIndex: "month", width: 180 },
      { title: t(`${props.i18nScope}.table_sales`), key: "sales", dataIndex: "sales", width: 150, align: "center" as const },
      {
        title: t(`${props.i18nScope}.table_sales_return`),
        key: "sales_returns",
        dataIndex: "sales_returns",
        width: 170,
        align: "center" as const,
      },
      { title: t(`${props.i18nScope}.table_income`), key: "income", dataIndex: "income", width: 150, align: "center" as const },
      { title: t(`${props.i18nScope}.table_purchases`), key: "purchases", dataIndex: "purchases", width: 170, align: "center" as const },
      { title: t(`${props.i18nScope}.table_expenses`), key: "expenses", dataIndex: "expenses", width: 160, align: "center" as const },
      { title: t(`${props.i18nScope}.table_net_profit`), key: "net_profit", dataIndex: "net_profit", width: 170, align: "center" as const },
    ]);

    const monetaryColumns = ["sales", "sales_returns", "income", "purchases", "expenses", "net_profit"];

    const formatNumber = (num: number) =>
      Number(num).toLocaleString(locale.value === "km" ? "km-KH" : "en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });

    const monthKeys: Record<string, string> = {
      january: "month_january",
      jan: "month_january",
      february: "month_february",
      feb: "month_february",
      march: "month_march",
      mar: "month_march",
      april: "month_april",
      apr: "month_april",
      may: "month_may",
      june: "month_june",
      jun: "month_june",
      july: "month_july",
      jul: "month_july",
      august: "month_august",
      aug: "month_august",
      september: "month_september",
      sep: "month_september",
      october: "month_october",
      oct: "month_october",
      november: "month_november",
      nov: "month_november",
      december: "month_december",
      dec: "month_december",
      lifetime: "lifetime",
      total: "month_total",
    };

    const formatMonth = (value?: string) => {
      const text = String(value || "");
      const key = monthKeys[text.toLowerCase()];
      return key ? t(`${props.i18nScope}.${key}`) : text;
    };

    const getMoneyClass = (key: string, record: ProfitLossReportItem) => {
      if (key === "net_profit") {
        return Number(record.net_profit) >= 0 ? "text-success fw-bold" : "text-danger fw-bold";
      }
      if (key === "sales" || key === "income") {
        return "text-success";
      }
      return "text-danger";
    };

    return {
      columns,
      monetaryColumns,
      formatNumber,
      formatMonth,
      getMoneyClass,
    };
  },
});
</script>

<style scoped>
.summary-card {
  height: 100%;
  padding: 18px 20px;
  border-radius: 14px;
  background: #fff;
  border: 1px solid #e6ebf2;
  box-shadow: 0 10px 24px rgba(15, 23, 42, 0.04);
}

.profit-card {
  background: linear-gradient(135deg, rgba(93, 95, 239, 0.08), rgba(93, 95, 239, 0.02));
}

.summary-label {
  display: block;
  margin-bottom: 8px;
  color: #6b7280;
  font-size: 13px;
  font-weight: 600;
}

.summary-value {
  font-size: 24px;
  line-height: 1.2;
}

.income-note {
  padding: 12px 16px;
  border-radius: 10px;
  background: #fff7e8;
  color: #b26a00;
  border: 1px solid #ffe2b8;
  font-size: 13px;
  font-weight: 500;
}

.profit-loss-table :deep(.ant-table-cell) {
  vertical-align: middle;
}

.profit-loss-table :deep(.ant-table-thead > tr > th) {
  white-space: nowrap;
}
</style>
